import React from "react";
import { Container, Row, Col } from "react-bootstrap";

const Terms = () => {
  return (
    <Container className="py-5">
      <Row>
        <Col lg={10} className="mx-auto">
          <h1 className="mb-4 text-center">
            AIRCLIFT CAPITAL TERMS AND CONDITIONS
          </h1>
          <p className="text-muted mb-5 text-center">
            Last updated: {new Date().getFullYear()}
          </p>

          <div className="mb-4">
            <h4 className="text-primary">INDEX</h4>
            <ol className="list-unstyled">
              <li>1. Introduction & Definitions</li>
              <li>2. Account Application & Requirements</li>
              <li>3. Account Types & Funding</li>
              <li>4. Trading Terms & Conditions</li>
              <li>5. Deposits & Withdrawals</li>
              <li>6. Company Rights & Discretionary Powers</li>
              <li>7. Risk Disclosure & Market Disclaimer</li>
              <li>8. Exclusion of Liability</li>
              <li>9. Termination & Account Suspension</li>
              <li>10. Amendments to Terms & Conditions</li>
              <li>11. Governing Law & Jurisdiction</li>
            </ol>
          </div>

          <hr className="my-5" />

          <div className="mt-4">
            <h3 className="text-primary">1. INTRODUCTION & DEFINITIONS</h3>
            <p>
              Welcome to AirClift Capital ("the Platform," "we," "us," or
              "our"), a sophisticated cryptocurrency and commodities trading
              platform. These Terms and Conditions ("Terms") constitute a
              legally binding agreement between you ("Client," "user," or
              "trader") and AirClift Capital, governing your access to and use
              of our proprietary trading technologies, including automated
              trading bots, scalping strategies, and institutional-grade
              commodities options trading.
            </p>
            <p>
              By registering an account, depositing funds, or engaging in any
              trading activity, you acknowledge that you have read, understood,
              and agreed to be bound by these Terms in their entirety. If you do
              not agree with any provision herein, you must immediately cease
              using our services.
            </p>
            <p>
              <strong>Key Definitions:</strong>
            </p>
            <ul>
              <li>
                <strong>"Client"</strong> refers to any individual, corporate
                entity, or institutional trader registered on the Platform.
              </li>
              <li>
                <strong>"Trading Bot"</strong> denotes our proprietary
                algorithmic trading system that executes trades autonomously
                based on predefined strategies.
              </li>
              <li>
                <strong>"Scalping"</strong> is a high-frequency trading strategy
                characterized by rapid buy-sell executions to capitalize on
                minor price fluctuations.
              </li>
              <li>
                <strong>"CTE (Company Trading Equity)"</strong> represents a
                promotional bonus credited to a user's account, which may be
                used for trading but is not redeemable for cash.
              </li>
            </ul>
          </div>

          <div className="mt-4">
            <h3 className="text-primary">
              2. ACCOUNT APPLICATION & REQUIREMENTS
            </h3>

            <h5>2.1 Personal Accounts</h5>
            <p>
              To ensure compliance with UK financial regulations and anti-fraud
              measures, all personal account applicants must meet the following
              criteria:
            </p>
            <ul>
              <li>
                Be at least 18 years of age (or the legal age of majority in
                their jurisdiction).
              </li>
              <li>
                Provide a valid government-issued photo ID (passport, national
                ID card, or driver's license).
              </li>
              <li>
                Submit proof of residence, such as a recent utility bill or bank
                statement (no older than three months).
              </li>
              <li>
                Only <strong>one account per individual is permitted</strong>{" "}
                unless prior written authorization is granted by AirClift
                Capital.
              </li>
            </ul>
            <p>
              We reserve the right to reject applications at our sole
              discretion, particularly if submitted documentation appears
              fraudulent or insufficient.
            </p>

            <h5>2.2 Corporate Accounts</h5>
            <p>
              Corporate clients must furnish additional documentation to verify
              the legitimacy of their business operations:
            </p>
            <ul>
              <li>
                Certificate of Incorporation (or equivalent company registration
                document).
              </li>
              <li>
                Memorandum & Articles of Association (or equivalent
                constitutional documents).
              </li>
              <li>
                Register of Directors & Ultimate Beneficial Owners (UBOs)
                (identifying all individuals holding 10%+ ownership).
              </li>
              <li>
                Corporate bank statement (confirming the entity's financial
                standing).
              </li>
            </ul>
            <p>
              Corporate accounts may be subject to enhanced due diligence (EDD)
              checks, particularly for politically exposed persons (PEPs) or
              high-risk jurisdictions.
            </p>

            <h5>2.3 Account Verification (KYC/AML)</h5>
            <p>
              AirClift Capital adheres strictly to UK Anti-Money Laundering
              (AML) and Counter-Terrorist Financing (CTF) regulations. As such:
            </p>
            <ul>
              <li>
                All clients must complete Know Your Customer (KYC) verification
                before trading.
              </li>
              <li>
                We may request additional documentation (e.g., source of funds
                declarations) at any time.
              </li>
              <li>
                Failure to comply with verification requests may result in
                account suspension or termination.
              </li>
            </ul>
          </div>

          <div className="mt-4">
            <h3 className="text-primary">3. ACCOUNT TYPES & FUNDING</h3>

            <h5>3.1 Retail Accounts</h5>
            <p>
              Our retail trading tiers are structured to accommodate varying
              levels of capital and trading sophistication:
            </p>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th>Minimum Deposit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Bronze</td>
                    <td>$5,000</td>
                  </tr>
                  <tr>
                    <td>Silver</td>
                    <td>$15,000</td>
                  </tr>
                  <tr>
                    <td>Gold</td>
                    <td>$25,000</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              <em>
                (See Account Types in website for features of each account type)
              </em>
            </p>

            <h5>3.2 Institutional Accounts</h5>
            <p>
              Designed for high-net-worth individuals, hedge funds, and
              corporate traders:
            </p>
            <ul>
              <li>Minimum deposit: $100,000</li>
              <li>Direct Market Access (DMA) for commodities options.</li>
              <li>
                Customizable algorithmic strategies tailored to institutional
                risk parameters.
              </li>
              <li>Reduced Commissions</li>
            </ul>
            <p>
              <em>
                (See Account Types in website for features of each account type)
              </em>
            </p>

            <h5>3.3 Company Trading Equity (CTE) & Bonuses</h5>
            <p>
              AirClift Capital may, at its discretion, offer CTE bonuses to
              incentivize trading activity:
            </p>
            <ul>
              <li>
                CTE funds are non-withdrawable—only profits generated from them
                may be withdrawn.
              </li>
              <li>
                Trading volume requirements may apply before bonus profits are
                released.
              </li>
              <li>
                We reserve the right to modify or revoke bonuses if terms are
                violated.
              </li>
            </ul>
          </div>

          <div className="mt-4">
            <h3 className="text-primary">4. TRADING TERMS & CONDITIONS</h3>
            <h5>4.1 Trading Bot & Strategies</h5>
            <p>
              Our proprietary trading bot operates autonomously, executing
              trades based on real-time market data and algorithmic scalping
              strategies.
            </p>
            <ul>
              <li>
                The bot's performance is not guaranteed—past results do not
                indicate future returns.
              </li>
            </ul>
            <h5>4.2 Cryptocurrency Trading (Scalping)</h5>
            <p>
              The bot specializes in high-frequency crypto-to-crypto trades,
              such as:
            </p>
            <ul>
              <li>BTC/ETH, SOL/USDT, XRP/BTC</li>
              <li>Ultra-short holding periods (seconds to minutes).</li>
            </ul>
            <h5>4.3 Commodities Trading (Options Contracts)</h5>
            <p>
              We exclusively trade options contracts (no spot/futures),
              leveraging institutional liquidity pools for:
            </p>
            <ul>
              <li>Gold, Oil, Silver, and other major commodities.</li>
              <li>European-style options (exercisable only at expiry).</li>
            </ul>
            <h5>4.4 Leverage & Margin Exposure</h5>
            <ul>
              <li>
                Leverage is applied to enhance position sizing, but margin calls
                can only occur if your broker explicitly asks your consent to
                move forward on an opportunity where such outcome is a
                possibility.
              </li>
              <li>
                Clients must maintain sufficient equity to avoid forced
                liquidations, only if participating on margin based investing,
                which would only happen with prior communication by your broker
                and you consent of moving forward with said action.
              </li>
            </ul>
            <h5>
              4.5 Profit Sharing & Performance Commissions (15% Retail Accounts
              | 10% Institutional Accounts)
            </h5>

            <ul>
              <li>
                AirClift Capital operates on a performance-based commission
                model—you are only invoiced when your account generates profits.
                This ensures our success is measured entirely by yours.
              </li>
              <li>
                How It Works When your account realizes profits, a performance
                commission is calculated based on your account type (15% for
                retail accounts, 10% for institutional accounts). This
                commission is invoiced separately and settled independently from
                your trading capital, ensuring your full balance remains
                actively deployed in the market.
              </li>
              <li>
                Payout Process Profit distributions become available six months
                after account activation, allowing our AI system to complete its
                strategic trading cycles and optimize long-term performance.
                Once your account reaches the payout eligibility window, any
                outstanding performance commissions are settled, after which
                your profits are promptly released. By maintaining commissions
                separately from your trading capital, your entire investment
                continues working for you—maximizing compounding potential and
                overall returns.
              </li>
            </ul>
          </div>

          <div className="mt-4">
            <h3 className="text-primary">5. DEPOSITS & WITHDRAWALS</h3>

            <h5>5.1 Accepted Payment Methods - Deposits & Withdrawals</h5>
            <ul>
              <li>
                Cryptocurrencies: BTC, ETH, USDT, USDC, and other major coins.
              </li>
              <li>
                Bank Transfers: GBP, USD, EUR (must match account holder name)
              </li>
            </ul>

            <h5>5.2 Withdrawal Policies</h5>
            <p>
              <strong>The Collective Capital Model</strong>
            </p>
            <p>
              At the core of our investment platform lies a proprietary
              algorithmic trading system (the "Trading Bot") designed to
              maximize returns through advanced quantitative strategies,
              including high-frequency trading, arbitrage, and machine
              learning-driven market predictions. Unlike traditional investment
              funds, our system operates most efficiently when investor capital
              remains pooled for extended periods, allowing the bot to:
            </p>
            <ol>
              <li>
                <strong>Optimize Liquidity Utilization</strong> – Capital must
                remain available to seize fleeting market opportunities, such as
                price discrepancies or sudden volatility spikes, which require
                immediate deployment of funds.
              </li>
              <li>
                <strong>Reduce Market Impact</strong> – Large or premature
                withdrawals force the bot to unwind positions prematurely,
                potentially causing slippage and diminishing returns for all
                participants.
              </li>
              <li>
                <strong>Compound Gains Effectively</strong> – Many strategies
                rely on reinvestment cycles; early withdrawals disrupt
                compounding efficiency.
              </li>
              <li>
                <strong>Maintain Hedging Integrity</strong> – The bot
                dynamically balances risk exposure across the entire capital
                pool. Removing funds abruptly may leave remaining investors
                overexposed.
              </li>
            </ol>
            <p>
              To ensure fairness and sustainability, withdrawal restrictions and
              penalties are implemented to discourage actions that could
              destabilize the system.
            </p>

            <h5>Section 1: Retail Account Withdrawal Policy</h5>
            <h6>5.1.1 Lock-In Period</h6>
            <p>
              Retail investors agree that, upon account activation, their
              capital will remain fully committed for a minimum of six (6)
              consecutive months before becoming eligible for withdrawal. This
              period allows the Trading Bot to integrate the funds into its
              broader strategy matrix, ensuring optimal performance without
              disruptive capital fluctuations. In the same way, AirClift Capital
              cannot collect their 30% profit-sharing portion, until the (6)
              months have occurred since the Client's account activation.
            </p>

            <h6>5.1.2 Early Withdrawal Penalty (35% of Total Account Value)</h6>
            <p>
              If a retail account holder submits a withdrawal request before the
              six-month lock-in period has elapsed, the following conditions
              apply:
            </p>
            <ul>
              <li>
                A penalty of 35% of the account's total value (calculated at the
                time of the request) will be deducted.
              </li>
              <li>
                AirClift capital would become immediately eligible for their
                portion of their profit share in the event this occurs
              </li>
              <li>
                This penalty is applied regardless of the requested withdrawal
                amount (partial or full).
              </li>
            </ul>
            <p>
              Early withdrawals create inefficiencies in the Trading Bot's
              operations, including:
            </p>
            <ul>
              <li>
                <strong>Forced Position Liquidation</strong> – The bot may need
                to close profitable trades prematurely to meet withdrawal
                demands.
              </li>
              <li>
                <strong>Reduced Leverage Capacity</strong> – Sudden capital
                outflows may limit the bot's ability to execute leveraged
                strategies.
              </li>
              <li>
                <strong>Administrative Rebalancing Costs</strong> – The platform
                must recalibrate risk models and liquidity reserves, incurring
                indirect costs.
              </li>
            </ul>

            <h5>Section 2: Institutional Account Withdrawal Policy</h5>
            <h6>5.1.3 Lock-In Period (12 Months Minimum)</h6>
            <p>
              Institutional accounts (including high-net-worth individuals,
              corporate investors, and fund partners) are subject to a twelve
              (12)-month lock-in period from the date of activation or
              institutional upgrade. Just like for the retail accounts, in the
              Institutional Account, AirClift Capital can't access their portion
              of the profit sharing, until the (12)-month lock-in period has
              been finalized. This extended duration is necessary due to:
            </p>
            <ul>
              <li>
                <strong>Larger Capital Allocations</strong> – Institutional
                deposits often comprise significant portions of the Trading
                Bot's working capital.
              </li>
              <li>
                <strong>Complex Strategy Integration</strong> – Institutional
                funds are frequently allocated to longer-term strategies (e.g.,
                arbitrage loops, algorithmic market-making).
              </li>
              <li>
                <strong>Systemic Risk Mitigation</strong> – Sudden large
                withdrawals could destabilize liquidity across the entire
                platform.
              </li>
            </ul>

            <h6>5.1.4 Early Withdrawal Penalty (50% of Total Account Value)</h6>
            <p>
              Institutional clients requesting withdrawals before the 12-month
              term expires will incur:
            </p>
            <ul>
              <li>
                AirClift capital would become immediately eligible for their
                portion of their profit share in the event this occurs
              </li>
            </ul>
            <p>
              Institutional withdrawals impose a disproportionate strain on the
              Trading Bot's ecosystem, including:
            </p>
            <ul>
              <li>
                <strong>Liquidity Crunch Risks</strong> – Large redemptions may
                force fire sales of assets, harming overall performance.
              </li>
              <li>
                <strong>Strategy Disruptions</strong> – Institutional capital is
                often tied to multi-phase trades; early removal can leave
                positions unhedged.
              </li>
            </ul>
            <p>
              The 50% penalty reflects the greater systemic impact of
              institutional withdrawals compared to retail.
            </p>

            <h5>Section 3: Additional Provisions</h5>
            <h6>
              5.1.5 Exceptions (Force Majeure & Extraordinary Circumstances)
            </h6>
            <p>
              In rare cases (e.g., legal insolvency, regulatory action, or
              platform failure), early withdrawal penalties may be waived or
              reduced at the sole discretion of the Company. Clients must submit
              formal documentation for review.
            </p>

            <h6>5.1.6 Post-Lock-In Withdrawals</h6>
            <p>After the lock-in period expires:</p>
            <ul>
              <li>Crypto withdrawals are processed within 24-48 hours</li>
              <li>
                Bank wire withdrawals are processed within 3 - 5 business days.
                Due to AML and CTF regulations, the funds would need to go back
                to the same account that your trading account got funded from
              </li>
            </ul>

            <h6>5.1.7 Transparency & Reporting</h6>
            <p>To reinforce trust, institutional clients receive:</p>
            <ul>
              <li>Quarterly liquidity audits showing capital deployment.</li>
              <li>
                Stress-test summaries demonstrating how withdrawals could impact
                the system.
              </li>
            </ul>
          </div>

          <div className="mt-4">
            <h3 className="text-primary">
              6. COMPANY RIGHTS & DISCRETIONARY POWERS
            </h3>
            <p>We reserve the right to:</p>
            <ul>
              <li>
                Modify trading parameters(e.g., leverage, asset availability).
              </li>
              <li>Suspend accounts - suspected of fraudulent activity.</li>
              <li>Adjust CTE bonus terms - at any time.</li>
            </ul>
          </div>

          <div className="mt-4">
            <h3 className="text-primary">
              7. RISK DISCLOSURE & MARKET DISCLAIMER
            </h3>
            <ul>
              <li>Trading involves risk—only risk capital should be used.</li>
              <li>We do not control market movements—losses may occur</li>
            </ul>
          </div>

          <div className="mt-4">
            <h3 className="text-primary">8. EXCLUSION OF LIABILITY</h3>
            <p>
              AirClift Capital and its affiliates, directors, employees, and
              agents shall not, under any circumstances, be held liable for any
              direct, indirect, incidental, consequential, or punitive damages
              arising from or related to:
            </p>

            <h5>8.1 Trading & Market Risks</h5>
            <ul>
              <li>
                Losses incurred due to market volatility, slippage, or liquidity
                shortages.
              </li>
              <li>
                Unforeseen black swan events (e.g., exchange hacks, regulatory
                crackdowns, geopolitical crises).
              </li>
              <li>
                Inaccurate price feeds, latency issues, or execution delays
                caused by third-party data providers.
              </li>
            </ul>

            <h5>8.2 Technical & Operational Failures</h5>
            <ul>
              <li>
                System outages, server crashes, or software malfunctions
                disrupting trading activity.
              </li>
              <li>
                Cyberattacks, hacking incidents, or unauthorized access to
                client accounts.
              </li>
            </ul>

            <h5>8.3 Third-Party Services</h5>
            <ul>
              <li>
                Banking delays, payment processor errors, or blockchain network
                congestion affecting withdrawals.
              </li>
              <li>
                <strong>Misconduct by external custodians</strong> (e.g.,
                mismanagement of client assets).
              </li>
            </ul>

            <h5>8.4 Client-Side Issues</h5>
            <ul>
              <li>
                Unauthorized account access due to weak passwords or phishing
                attacks.
              </li>
              <li>
                Failure to comply with margin requirements, resulting in forced
                liquidations.
              </li>
            </ul>

            <h5>8.5 No Guarantee of Performance</h5>
            <ul>
              <li>
                Historical trading results are not indicative of future returns.
              </li>
              <li>
                The Platform does not guarantee profits or protection against
                losses.
              </li>
              <li>
                Algorithmic strategies may underperform in certain market
                conditions.
              </li>
            </ul>
          </div>

          <div className="mt-4">
            <h3 className="text-primary">
              9. TERMINATION & ACCOUNT SUSPENSION
            </h3>
            <p>Accounts may be terminated for:</p>
            <ul>
              <li>Violating these Terms.</li>
              <li>Suspicious trading activity.</li>
            </ul>
          </div>

          <div className="mt-4">
            <h3 className="text-primary">
              10. AMENDMENTS TO TERMS & CONDITIONS
            </h3>
            <p>
              We may update these Terms periodically. Continued use constitutes
              acceptance.
            </p>
          </div>

          <div className="mt-4">
            <h3 className="text-primary">11. GOVERNING LAW & JURISDICTION</h3>

            <h5>11.1 Applicable Law</h5>
            <p>
              These Terms and Conditions, and any disputes arising from them,
              shall be governed by and construed in accordance with the laws of
              England and Wales, without regard to its conflict of laws
              principles.
            </p>

            <h5>11.2 Exclusive Jurisdiction</h5>
            <p>
              Any legal action, claim, or dispute related to AirClift Capital
              shall be subject to the exclusive jurisdiction of the courts of
              England and Wales. Clients irrevocably submit to this jurisdiction
              and waive any objections based on forum non conveniens or similar
              doctrines.
            </p>

            <h5>11.3 Arbitration (Optional)</h5>
            <ul>
              <li>
                For institutional clients or disputes exceeding £100,000,
                parties may agree to resolve conflicts through binding
                arbitration under the London Court of International Arbitration
                (LCIA) rules.
              </li>
              <li>
                Arbitration proceedings shall be confidential and held in
                London, UK.
              </li>
              <li>
                The arbitrator's decision shall be final and enforceable in any
                competent court.
              </li>
            </ul>

            <h5>11.4 Waiver of Class Actions</h5>
            <p>
              Clients waive any right to participate in class-action lawsuits or
              consolidated claims against AirClift Capital, agreeing to pursue
              disputes individually.
            </p>

            <h5>11.5 Regulatory Compliance</h5>
            <ul>
              <li>
                AirClift Capital operates under{" "}
                <strong>UK financial regulations</strong>, including but not
                limited to:
              </li>
              <li>
                Financial Conduct Authority (FCA) guidelines (where applicable).
              </li>
              <li>
                Anti-Money Laundering (AML) and Counter-Terrorist Financing
                (CTF) laws.
              </li>
              <li>
                Non-UK residents remain bound by these Terms but acknowledge
                that local laws may impose additional restrictions.
              </li>
            </ul>

            <h5>11.6 Severability</h5>
            <p>
              If any provision of these Terms is deemed invalid or
              unenforceable, the remaining provisions shall remain in full force
              and effect.
            </p>
          </div>

          <div className="mt-5 p-4 bg-light rounded">
            <p className="text-center mb-0">
              <strong>
                By using AirClift Capital, you acknowledge that you have read,
                understood, and agreed to these Terms and Conditions in full.
              </strong>
            </p>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Terms;
